import React, { useState } from 'react';
import { TableFull } from './Shared';

// ─────────────────────────────────────────────────────────────────────────────
// TechnicalMasters.js  — Engineering data for SMV calculation
// Tabs: Machines | Manual Code Groups | Manual Codes | Stoppages |
//       Fabric Difficulty | Allowances
// Fabric names are IMPORTED from Global Masters (db.fabrics) — not entered here.
// Manual codes belong to a Code Group so multiple systems (GSD, MTM, etc.) can
// coexist; one group is flagged as the company default.
// ─────────────────────────────────────────────────────────────────────────────

const API = 'https://nirvikalp.integrashion.com/api/save_universal.php';

async function saveToDB(category, type, data) {
  try {
    const res = await fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, type, data }),
    });
    const r = await res.json();
    return r.id || Date.now();
  } catch (e) { console.error('Save failed', e); return Date.now(); }
}

async function deleteToDB(category, type, id) {
  try {
    await fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, type, data: { id, _deleted: true } }),
    });
  } catch (e) { console.error('Delete failed', e); }
}

export default function TechnicalMasters({ db, setDb }) {
  const [tab, setTab] = useState('machines');
  const [editId, setEditId] = useState(null);

  // ── Form states ────────────────────────────────────────────────────────────
  const blankMc  = { code:'', name:'', rpm:5000, spc:5, spi:12, allowance:10, threadFactor:3, wastageFactor:15 };
  const blankMn  = { code:'', name:'', desc:'', timeValue:0, groupId:'' };
  const blankGrp = { name:'', description:'', isDefault:false };
  const blankSt  = { code:'', factor:5 };
  const blankFd  = { fabricId:'', factor:0, range:'' };  // fabricId from db.fabrics
  const blankAl  = { dept:'', name:'', factor:5 };
  const blankTrim= { name:'' };
  const blankSd  = { name:'', code:'', description:'', image:'', machineHint:'', manualHint:'' };
  const blankUom = { name:'' };
  const blankVa  = { name:'' };

  const [mcIn,  setMcIn]  = useState(blankMc);
  const [mnIn,  setMnIn]  = useState(blankMn);
  const [grpIn, setGrpIn] = useState(blankGrp);
  const [stIn,  setStIn]  = useState(blankSt);
  const [fdIn,  setFdIn]  = useState(blankFd);
  const [alIn,  setAlIn]  = useState(blankAl);
  const [genVal, setGenVal] = useState('');
  const [viewGroup, setViewGroup] = useState('');
  const [sdIn, setSdIn]           = useState({ name:'', code:'', description:'', image:'', machineHint:'', manualHint:'' }); // filter for manual codes list

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const upsert = (list, entry, id) =>
    id ? (list || []).map(x => x.id === id ? entry : x) : [...(list || []), entry];

  const del = (list, id) => (list || []).filter(x => x.id !== id);

  const confirmDel = (listKey, id, type, cat = 'tech') => {
    if (!window.confirm('Delete this entry?')) return;
    setDb(p => ({ ...p, [listKey]: del(p[listKey], id) }));
    deleteToDB(cat, type, id);
  };

  const startEdit = (setter, item) => { setter(item); setEditId(item.id); };
  const cancelEdit = (setter, blank) => { setter(blank); setEditId(null); };

  // ─── Save handlers ────────────────────────────────────────────────────────
  const saveMachine = async () => {
    if (!mcIn.code) return alert('Machine code / name required');
    const id = await saveToDB('tech', 'MACHINE', mcIn);
    const entry = { ...mcIn, id: editId || id };
    setDb(p => ({ ...p, machines: upsert(p.machines, entry, editId) }));
    setMcIn(blankMc); setEditId(null);
  };

  // Code group — stored in common_masters type=CODE_GROUP
  const saveGroup = async () => {
    if (!grpIn.name) return alert('Group name required');
    // If this group is being set as default, clear all others
    if (grpIn.isDefault) {
      for (const g of (db.codeGroups || [])) {
        if (g.id !== editId && g.isDefault) {
          await saveToDB('common', 'CODE_GROUP', { ...g, isDefault: false });
        }
      }
      setDb(p => ({ ...p, codeGroups: (p.codeGroups || []).map(g => ({ ...g, isDefault: false })) }));
    }
    const id = await saveToDB('common', 'CODE_GROUP', grpIn);
    const entry = { ...grpIn, id: editId || id };
    setDb(p => ({ ...p, codeGroups: upsert(p.codeGroups, entry, editId) }));
    setGrpIn(blankGrp); setEditId(null);
  };

  const saveManual = async () => {
    if (!mnIn.code) return alert('Code required');
    if (!mnIn.groupId) return alert('Select a Code Group first');
    const id = await saveToDB('tech', 'MANUAL', mnIn);
    const entry = { ...mnIn, id: editId || id };
    setDb(p => ({ ...p, manualCodes: upsert(p.manualCodes, entry, editId) }));
    setMnIn(blankMn); setEditId(null);
  };

  const saveStoppage = async () => {
    if (!stIn.code) return alert('Code required');
    const id = await saveToDB('tech', 'STOPPAGE', stIn);
    const entry = { ...stIn, id: editId || id };
    setDb(p => ({ ...p, stoppages: upsert(p.stoppages, entry, editId) }));
    setStIn(blankSt); setEditId(null);
  };

  const saveFabricDiff = async () => {
    if (!fdIn.fabricId) return alert('Select a fabric from Global Masters');
    const fabricObj = (db.fabrics || []).find(f => String(f.id) === String(fdIn.fabricId));
    const entry_data = { ...fdIn, fabric: fabricObj?.name || fdIn.fabricId };
    const id = await saveToDB('tech', 'FABRIC_DIFF', entry_data);
    const entry = { ...entry_data, id: editId || id };
    setDb(p => ({ ...p, fabricDifficulties: upsert(p.fabricDifficulties, entry, editId) }));
    setFdIn(blankFd); setEditId(null);
  };

  const saveAllowance = async () => {
    if (!alIn.name) return alert('Name required');
    const id = await saveToDB('tech', 'ALLOWANCE', alIn);
    const entry = { ...alIn, id: editId || id };
    setDb(p => ({ ...p, allowances: upsert(p.allowances, entry, editId) }));
    setAlIn(blankAl); setEditId(null);
  };

  const saveGeneric = async (listKey, type) => {
    if (!genVal.trim()) return alert('Name required');
    const typeMap = { trimTypes:'TRIM_TYPE', uoms:'UOM', vaProcesses:'VA_PROCESS' };
    const id = await saveToDB('common', typeMap[listKey] || type, { name: genVal.trim() });
    const entry = { id: editId || id, name: genVal.trim() };
    setDb(p => ({ ...p, [listKey]: upsert(p[listKey], entry, editId) }));
    setGenVal(''); setEditId(null);
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const sideBtn = (t) => ({
    textAlign:'left', width:'100%', padding:'10px 18px', border:'none',
    borderBottom:'1px solid #f1f5f9', cursor:'pointer',
    background: tab === t ? '#eff6ff' : 'transparent',
    color: tab === t ? '#2563eb' : '#475569',
    borderLeft: tab === t ? '4px solid #2563eb' : '4px solid transparent',
    fontSize:'0.82rem', fontWeight: tab === t ? 700 : 400,
  });
  const inp = { padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:'0.82rem', background:'white' };
  const btn = (bg='#2563eb', col='white') => ({
    padding:'7px 16px', background:bg, color:col, border:'none',
    borderRadius:6, cursor:'pointer', fontSize:'0.82rem', fontWeight:600, whiteSpace:'nowrap',
  });
  const card = { background:'white', borderRadius:10, padding:24, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' };
  const h3s  = { margin:'0 0 16px', fontSize:'0.9rem', fontWeight:700, color:'#1e293b', textTransform:'uppercase', letterSpacing:'.04em' };
  const lbl  = { fontSize:'0.7rem', fontWeight:700, color:'#64748b', marginBottom:4, display:'block', textTransform:'uppercase', letterSpacing:'.04em' };

  const groups = db.codeGroups || [];
  const defaultGroup = groups.find(g => g.isDefault);

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>

      {/* ── Sidebar ── */}
      <div style={{ width:220, background:'white', borderRight:'1px solid #e2e8f0', display:'flex', flexDirection:'column', overflowY:'auto', flexShrink:0 }}>
        <div style={{ padding:'10px 16px', background:'#f8fafc', fontSize:'0.68rem', fontWeight:700, color:'#94a3b8', letterSpacing:'.08em' }}>GENERAL</div>
        {['trimTypes','uoms','vaProcesses'].map(t =>
          <button key={t} style={sideBtn(t)} onClick={() => { setTab(t); cancelEdit(setGenVal, ''); }}>
            {t === 'trimTypes' ? 'Trim Types' : t === 'uoms' ? 'Units of Measure' : 'VA Processes'}
          </button>
        )}
        <div style={{ padding:'10px 16px', background:'#f8fafc', fontSize:'0.68rem', fontWeight:700, color:'#94a3b8', letterSpacing:'.08em', borderTop:'1px solid #e2e8f0' }}>ENGINEERING</div>
        <button style={sideBtn('machines')}   onClick={() => { setTab('machines');   cancelEdit(setMcIn, blankMc); }}>Machine Database</button>
        <button style={sideBtn('codeGroups')} onClick={() => { setTab('codeGroups');cancelEdit(setGrpIn, blankGrp); }}>
          Code Groups
          {defaultGroup && <span style={{ marginLeft:6, fontSize:'0.65rem', background:'#dbeafe', color:'#2563eb', padding:'1px 6px', borderRadius:10 }}>Default: {defaultGroup.name}</span>}
        </button>
        <button style={sideBtn('manual')}      onClick={() => { setTab('manual');    cancelEdit(setMnIn, blankMn); }}>Manual Codes</button>
        <button style={sideBtn('stoppages')}   onClick={() => { setTab('stoppages');cancelEdit(setStIn, blankSt); }}>Stoppage Factors</button>
        <button style={sideBtn('fabric')}      onClick={() => { setTab('fabric');   cancelEdit(setFdIn, blankFd); }}>Fabric Difficulty</button>
        <button style={sideBtn('allowances')}  onClick={() => { setTab('allowances');cancelEdit(setAlIn, blankAl); }}>Allowances</button>
        <button style={sideBtn('seamDiagrams')} onClick={() => { setTab('seamDiagrams');cancelEdit(setSdIn, blankSd); }}>Seam Diagrams</button>
      </div>

      {/* ── Content ── */}
      <div style={{ flex:1, padding:24, overflowY:'auto', background:'#f8fafc' }}>

        {/* Generic: trimTypes / uoms / vaProcesses */}
        {['trimTypes','uoms','vaProcesses'].includes(tab) && (
          <div style={card}>
            <h3 style={h3s}>{tab === 'trimTypes' ? 'Trim Types' : tab === 'uoms' ? 'Units of Measure' : 'VA Processes'}</h3>
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              <input style={{ ...inp, flex:1 }} placeholder="Name" value={genVal} onChange={e => setGenVal(e.target.value)} />
              <button style={btn()} onClick={() => saveGeneric(tab)}>{editId ? 'Update' : 'Add'}</button>
              {editId && <button style={btn('#f1f5f9','#475569')} onClick={() => cancelEdit(setGenVal,'')}>Cancel</button>}
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
              <thead><tr style={{ background:'#f8fafc' }}><th style={{ padding:'8px 10px', textAlign:'left', color:'#64748b', fontWeight:600, borderBottom:'2px solid #e2e8f0' }}>Name</th><th style={{ padding:'8px 10px', width:100, borderBottom:'2px solid #e2e8f0' }}>Actions</th></tr></thead>
              <tbody>{(db[tab]||[]).map(i => (
                <tr key={i.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                  <td style={{ padding:'8px 10px' }}>{i.name}</td>
                  <td style={{ padding:'8px 10px', display:'flex', gap:6 }}>
                    <button style={btn('#f1f5f9','#475569')} onClick={() => { setGenVal(i.name); setEditId(i.id); }}>Edit</button>
                    <button style={btn('#fef2f2','#dc2626')} onClick={() => confirmDel(tab, i.id, tab.toUpperCase().replace('TYPES','_TYPE'), 'common')}>Del</button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}

        {/* Machines */}
        {tab === 'machines' && (
          <div style={card}>
            <h3 style={h3s}>Machine Database</h3>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr 1fr', gap:8, marginBottom:8 }}>
              {['Machine Name/Code','RPM','SPC','SPI','Allow %','Thread Factor','Wastage %'].map(l =>
                <span key={l} style={lbl}>{l}</span>)}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr 1fr', gap:8, marginBottom:16 }}>
              <input style={inp} placeholder="e.g. SNLS" value={mcIn.code} onChange={e => setMcIn({...mcIn, code:e.target.value})} />
              <input style={inp} type="number" value={mcIn.rpm}          onChange={e => setMcIn({...mcIn, rpm:+e.target.value})} />
              <input style={inp} type="number" value={mcIn.spc}          onChange={e => setMcIn({...mcIn, spc:+e.target.value})} />
              <input style={inp} type="number" value={mcIn.spi}          onChange={e => setMcIn({...mcIn, spi:+e.target.value})} />
              <input style={inp} type="number" value={mcIn.allowance}    onChange={e => setMcIn({...mcIn, allowance:+e.target.value})} />
              <input style={inp} type="number" step="0.1" value={mcIn.threadFactor}  onChange={e => setMcIn({...mcIn, threadFactor:+e.target.value})} title="Thread length multiplier" />
              <input style={inp} type="number" value={mcIn.wastageFactor} onChange={e => setMcIn({...mcIn, wastageFactor:+e.target.value})} title="Thread wastage %" />
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              <button style={btn()} onClick={saveMachine}>{editId ? 'Update Machine' : 'Add Machine'}</button>
              {editId && <button style={btn('#f1f5f9','#475569')} onClick={() => cancelEdit(setMcIn, blankMc)}>Cancel</button>}
            </div>
            <TableFull data={db.machines||[]} cols={['code','rpm','spc','spi','allowance','threadFactor','wastageFactor']}
              onEdit={i => startEdit(setMcIn, i)} onDelete={id => confirmDel('machines', id, 'MACHINE')} />
          </div>
        )}

        {/* Code Groups */}
        {tab === 'codeGroups' && (
          <div style={card}>
            <h3 style={h3s}>Manual Code Groups</h3>
            <p style={{ color:'#64748b', fontSize:'0.82rem', marginBottom:20, lineHeight:1.6 }}>
              Group your manual codes by system (e.g. GSD, MTM, Your own codes). Up to 4 groups.
              Mark one as <strong>Default</strong> — it will pre-select when adding codes in PartDNA.
              Each style can override the default group to use a different system.
            </p>

            {groups.length < 4 || editId ? (
              <div style={{ background:'#f8fafc', borderRadius:8, padding:16, border:'1px solid #e2e8f0', marginBottom:20 }}>
                <div style={{ display:'grid', gridTemplateColumns:'2fr 3fr auto auto', gap:8, alignItems:'end' }}>
                  <div>
                    <span style={lbl}>Group Name *</span>
                    <input style={inp} placeholder="e.g. GSD, MTM, My Codes" value={grpIn.name} onChange={e => setGrpIn({...grpIn, name:e.target.value})} />
                  </div>
                  <div>
                    <span style={lbl}>Description</span>
                    <input style={inp} placeholder="Brief description" value={grpIn.description} onChange={e => setGrpIn({...grpIn, description:e.target.value})} />
                  </div>
                  <div style={{ paddingBottom:2 }}>
                    <span style={lbl}>Set as Default</span>
                    <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', paddingTop:6 }}>
                      <input type="checkbox" checked={!!grpIn.isDefault} onChange={e => setGrpIn({...grpIn, isDefault:e.target.checked})} />
                      <span style={{ fontSize:'0.82rem', color:'#475569' }}>Default for Nirvikalp</span>
                    </label>
                  </div>
                  <div style={{ paddingBottom:2, display:'flex', gap:6, alignItems:'flex-end' }}>
                    <button style={btn()} onClick={saveGroup}>{editId ? 'Update' : 'Add Group'}</button>
                    {editId && <button style={btn('#f1f5f9','#475569')} onClick={() => cancelEdit(setGrpIn, blankGrp)}>Cancel</button>}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ padding:'10px 14px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:6, marginBottom:20, color:'#dc2626', fontSize:'0.82rem' }}>
                Maximum 4 code groups reached. Delete an existing group to add a new one.
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:12 }}>
              {groups.map(g => {
                const codeCount = (db.manualCodes || []).filter(m => String(m.groupId) === String(g.id)).length;
                return (
                  <div key={g.id} style={{ border:`2px solid ${g.isDefault ? '#2563eb' : '#e2e8f0'}`, borderRadius:8, padding:16, background: g.isDefault ? '#eff6ff' : 'white', position:'relative' }}>
                    {g.isDefault && <span style={{ position:'absolute', top:10, right:10, background:'#2563eb', color:'white', fontSize:'0.65rem', fontWeight:700, padding:'2px 8px', borderRadius:10 }}>DEFAULT</span>}
                    <div style={{ fontWeight:700, fontSize:'0.95rem', color:'#1e293b', marginBottom:4 }}>{g.name}</div>
                    {g.description && <div style={{ fontSize:'0.78rem', color:'#64748b', marginBottom:8 }}>{g.description}</div>}
                    <div style={{ fontSize:'0.75rem', color:'#64748b', marginBottom:12 }}>{codeCount} code{codeCount !== 1 ? 's' : ''} in this group</div>
                    <div style={{ display:'flex', gap:6 }}>
                      <button style={btn('#f1f5f9','#475569')} onClick={() => { setGrpIn(g); setEditId(g.id); }}>Edit</button>
                      <button style={btn('#fef2f2','#dc2626')} onClick={() => {
                        if (codeCount > 0) return alert(`Cannot delete — ${codeCount} codes use this group. Reassign them first.`);
                        confirmDel('codeGroups', g.id, 'CODE_GROUP', 'common');
                      }}>Delete</button>
                    </div>
                  </div>
                );
              })}
              {groups.length === 0 && (
                <div style={{ padding:24, color:'#94a3b8', textAlign:'center', gridColumn:'1/-1' }}>
                  No code groups yet. Add your first group above.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Manual Codes */}
        {tab === 'manual' && (
          <div style={card}>
            <h3 style={h3s}>Manual Codes</h3>
            {groups.length === 0 && (
              <div style={{ padding:'10px 14px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:6, marginBottom:16, color:'#92400e', fontSize:'0.82rem' }}>
                Create at least one Code Group first (Engineering &rarr; Code Groups) before adding manual codes.
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 2fr 1fr auto', gap:8, alignItems:'end', marginBottom:8 }}>
              {['Code Group *','Code *','Description / Name','Time Value (s)',''].map(l => <span key={l} style={lbl}>{l}</span>)}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 2fr 1fr auto', gap:8, marginBottom:16 }}>
              <select style={inp} value={mnIn.groupId} onChange={e => setMnIn({...mnIn, groupId:e.target.value})}>
                <option value="">-- Group --</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}{g.isDefault ? ' (Default)' : ''}</option>)}
              </select>
              <input style={inp} placeholder="e.g. P1, GSD-101" value={mnIn.code} onChange={e => setMnIn({...mnIn, code:e.target.value})} />
              <input style={inp} placeholder="What this code does" value={mnIn.desc || mnIn.name || ''} onChange={e => setMnIn({...mnIn, desc:e.target.value, name:e.target.value})} />
              <input style={inp} type="number" step="0.01" placeholder="Seconds" value={mnIn.timeValue} onChange={e => setMnIn({...mnIn, timeValue:+e.target.value})} title="Pre-set time value in seconds for this manual code" />
              <div style={{ display:'flex', gap:6 }}>
                <button style={btn()} onClick={saveManual}>{editId ? 'Update' : 'Add'}</button>
                {editId && <button style={btn('#f1f5f9','#475569')} onClick={() => cancelEdit(setMnIn, blankMn)}>Cancel</button>}
              </div>
            </div>

            {/* Group filter for the list */}
            {groups.length > 0 && (() => {
              const filtered = (db.manualCodes||[]).filter(m => !viewGroup || String(m.groupId) === viewGroup);
              return (
                <>
                  <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:12 }}>
                    <span style={{ fontSize:'0.78rem', color:'#64748b' }}>Filter by group:</span>
                    {['', ...groups.map(g => g.id)].map(gid => {
                      const g = groups.find(x => String(x.id) === String(gid));
                      return (
                        <button key={gid} onClick={() => setViewGroup(gid)}
                          style={{ padding:'3px 12px', borderRadius:20, border:'1px solid', fontSize:'0.75rem', cursor:'pointer',
                            background: viewGroup === gid ? '#2563eb' : 'white',
                            color: viewGroup === gid ? 'white' : '#475569',
                            borderColor: viewGroup === gid ? '#2563eb' : '#e2e8f0' }}>
                          {g ? g.name : 'All'}
                        </button>
                      );
                    })}
                    <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:'#94a3b8' }}>{filtered.length} codes</span>
                  </div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                    <thead><tr style={{ background:'#f8fafc' }}>
                      {['Group','Code','Description','Time (s)','Actions'].map(h =>
                        <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'#64748b', fontWeight:600, borderBottom:'2px solid #e2e8f0' }}>{h}</th>)}
                    </tr></thead>
                    <tbody>{filtered.map(m => {
                      const g = groups.find(x => String(x.id) === String(m.groupId));
                      return (
                        <tr key={m.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                          <td style={{ padding:'8px 10px' }}>
                            <span style={{ background: g?.isDefault ? '#dbeafe' : '#f1f5f9', color: g?.isDefault ? '#2563eb' : '#475569', padding:'2px 8px', borderRadius:10, fontSize:'0.72rem', fontWeight:600 }}>{g?.name || '—'}</span>
                          </td>
                          <td style={{ padding:'8px 10px', fontWeight:600, fontFamily:'monospace' }}>{m.code}</td>
                          <td style={{ padding:'8px 10px', color:'#475569' }}>{m.name || m.desc || '—'}</td>
                          <td style={{ padding:'8px 10px', fontFamily:'monospace', color:'#059669' }}>{m.timeValue || '—'}</td>
                          <td style={{ padding:'8px 10px', display:'flex', gap:6 }}>
                            <button style={btn('#f1f5f9','#475569')} onClick={() => startEdit(setMnIn, m)}>Edit</button>
                            <button style={btn('#fef2f2','#dc2626')} onClick={() => confirmDel('manualCodes', m.id, 'MANUAL')}>Del</button>
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </>
              );
            })()}
          </div>
        )}

        {/* Stoppages */}
        {tab === 'stoppages' && (
          <div style={card}>
            <h3 style={h3s}>Stoppage Factors</h3>
            <p style={{ color:'#64748b', fontSize:'0.82rem', marginBottom:16 }}>Percentage added to machine time to account for stoppages (maintenance, thread breaks, etc.)</p>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr auto', gap:8, alignItems:'end', marginBottom:20 }}>
              <div><span style={lbl}>Stoppage Code</span><input style={inp} placeholder="e.g. REST, MAINT" value={stIn.code} onChange={e => setStIn({...stIn, code:e.target.value})} /></div>
              <div><span style={lbl}>Factor (%)</span><input style={inp} type="number" value={stIn.factor} onChange={e => setStIn({...stIn, factor:+e.target.value})} /></div>
              <div style={{ display:'flex', gap:6 }}>
                <button style={btn()} onClick={saveStoppage}>{editId ? 'Update' : 'Add'}</button>
                {editId && <button style={btn('#f1f5f9','#475569')} onClick={() => cancelEdit(setStIn, blankSt)}>Cancel</button>}
              </div>
            </div>
            <TableFull data={db.stoppages||[]} cols={['code','factor']}
              onEdit={i => startEdit(setStIn, i)} onDelete={id => confirmDel('stoppages', id, 'STOPPAGE')} />
          </div>
        )}

        {/* Fabric Difficulty — import fabric from Global Masters */}
        {tab === 'fabric' && (
          <div style={card}>
            <h3 style={h3s}>Fabric Difficulty Factors</h3>
            <p style={{ color:'#64748b', fontSize:'0.82rem', marginBottom:16 }}>
              Fabric names come from <strong>Global Masters &rarr; Fabrics</strong>. Add a difficulty factor (%) for each fabric type to adjust SMV calculation.
              {(db.fabrics || []).length === 0 && <span style={{ color:'#dc2626', marginLeft:8 }}>No fabrics found — add them in Global Masters first.</span>}
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'3fr 2fr 1fr auto', gap:8, alignItems:'end', marginBottom:20 }}>
              <div>
                <span style={lbl}>Fabric (from Global Masters) *</span>
                <select style={inp} value={fdIn.fabricId} onChange={e => setFdIn({...fdIn, fabricId:e.target.value})}>
                  <option value="">-- Select Fabric --</option>
                  {(db.fabrics || []).map(f => {
                    const name = f.name || f.fabric || '';
                    const existing = (db.fabricDifficulties||[]).find(d => String(d.fabricId) === String(f.id) || d.fabric === name);
                    return <option key={f.id} value={f.id}>{name}{existing ? ` (already set: ${existing.factor}%)` : ''}</option>;
                  })}
                </select>
              </div>
              <div>
                <span style={lbl}>Difficulty Range (optional)</span>
                <input style={inp} placeholder="e.g. Lightweight, Heavy" value={fdIn.range} onChange={e => setFdIn({...fdIn, range:e.target.value})} />
              </div>
              <div>
                <span style={lbl}>Factor (%)</span>
                <input style={inp} type="number" placeholder="0" value={fdIn.factor} onChange={e => setFdIn({...fdIn, factor:+e.target.value})} />
              </div>
              <div style={{ display:'flex', gap:6 }}>
                <button style={btn()} onClick={saveFabricDiff}>{editId ? 'Update' : 'Add'}</button>
                {editId && <button style={btn('#f1f5f9','#475569')} onClick={() => cancelEdit(setFdIn, blankFd)}>Cancel</button>}
              </div>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
              <thead><tr style={{ background:'#f8fafc' }}>
                {['Fabric','Range','Difficulty Factor (%)','Actions'].map(h =>
                  <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'#64748b', fontWeight:600, borderBottom:'2px solid #e2e8f0' }}>{h}</th>)}
              </tr></thead>
              <tbody>{(db.fabricDifficulties||[]).map(f => (
                <tr key={f.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                  <td style={{ padding:'8px 10px', fontWeight:600 }}>{f.fabric || f.name}</td>
                  <td style={{ padding:'8px 10px', color:'#64748b' }}>{f.range || '—'}</td>
                  <td style={{ padding:'8px 10px' }}>
                    <span style={{ background:'#fef3c7', color:'#92400e', padding:'2px 10px', borderRadius:10, fontWeight:700 }}>{f.factor}%</span>
                  </td>
                  <td style={{ padding:'8px 10px', display:'flex', gap:6 }}>
                    <button style={btn('#f1f5f9','#475569')} onClick={() => {
                      startEdit(setFdIn, { ...f, fabricId: f.fabricId || f.id });
                    }}>Edit</button>
                    <button style={btn('#fef2f2','#dc2626')} onClick={() => confirmDel('fabricDifficulties', f.id, 'FABRIC_DIFF')}>Del</button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}

        {/* Allowances */}
        {tab === 'allowances' && (
          <div style={card}>
            <h3 style={h3s}>Allowance Master</h3>
            <p style={{ color:'#64748b', fontSize:'0.82rem', marginBottom:16 }}>Personal allowances (fatigue, rest, etc.) added to process time per department.</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr 1fr auto', gap:8, alignItems:'end', marginBottom:20 }}>
              <div><span style={lbl}>Department</span><input style={inp} placeholder="e.g. Sewing, Cutting" value={alIn.dept} onChange={e => setAlIn({...alIn, dept:e.target.value})} /></div>
              <div><span style={lbl}>Allowance Name</span><input style={inp} placeholder="e.g. Personal, Fatigue" value={alIn.name} onChange={e => setAlIn({...alIn, name:e.target.value})} /></div>
              <div><span style={lbl}>Factor (%)</span><input style={inp} type="number" value={alIn.factor} onChange={e => setAlIn({...alIn, factor:+e.target.value})} /></div>
              <div style={{ display:'flex', gap:6 }}>
                <button style={btn()} onClick={saveAllowance}>{editId ? 'Update' : 'Add'}</button>
                {editId && <button style={btn('#f1f5f9','#475569')} onClick={() => cancelEdit(setAlIn, blankAl)}>Cancel</button>}
              </div>
            </div>
            <TableFull data={db.allowances||[]} cols={['dept','name','factor']}
              onEdit={i => startEdit(setAlIn, i)} onDelete={id => confirmDel('allowances', id, 'ALLOWANCE')} />
          </div>
        )}

        {/* Seam Diagrams */}
        {tab === 'seamDiagrams' && (
          <div style={card}>
            <h3 style={h3s}>Seam Diagram Library</h3>
            <p style={{ color:'#64748b', fontSize:'0.82rem', marginBottom:16 }}>
              Add seam cross-section diagrams to classify construction types. These are attached to processes in PartDNA
              and will be used to train AI for automatic machine/code suggestion and SMV prediction.
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 2fr 1fr 1fr', gap:8, alignItems:'end', marginBottom:8 }}>
              {['Code *','Name *','Description','Machine Hint','Manual Code Hint'].map(l=><span key={l} style={lbl}>{l}</span>)}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 2fr 1fr 1fr', gap:8, marginBottom:12 }}>
              <input style={inp} placeholder="e.g. SSa, LSa, BSa" value={sdIn.code} onChange={e=>setSdIn({...sdIn,code:e.target.value})} title="ISO/BS seam class code" />
              <input style={inp} placeholder="e.g. Plain Seam" value={sdIn.name} onChange={e=>setSdIn({...sdIn,name:e.target.value})} />
              <input style={inp} placeholder="e.g. Two pieces joined, sewn right sides together" value={sdIn.description} onChange={e=>setSdIn({...sdIn,description:e.target.value})} />
              <input style={inp} placeholder="e.g. SNLS, OL 3T" value={sdIn.machineHint} onChange={e=>setSdIn({...sdIn,machineHint:e.target.value})} title="Suggested machine type for this seam" />
              <input style={inp} placeholder="e.g. TRIM, PRESS" value={sdIn.manualHint} onChange={e=>setSdIn({...sdIn,manualHint:e.target.value})} title="Suggested manual operations" />
            </div>
            {/* Image upload for seam diagram */}
            <div style={{ marginBottom:16, display:'flex', gap:12, alignItems:'flex-start' }}>
              <div>
                <span style={lbl}>Seam Cross-Section Image</span>
                <label style={{ cursor:'pointer' }}>
                  <div style={{ width:140, height:90, border:`2px dashed ${sdIn.image?'#4f46e5':'#e2e8f0'}`, borderRadius:8, overflow:'hidden', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {sdIn.image
                      ? <img src={sdIn.image} alt="seam" style={{ width:'100%', height:'100%', objectFit:'contain' }} />
                      : <span style={{ fontSize:11, color:'#94a3b8', textAlign:'center', padding:8 }}>Click to upload diagram</span>}
                  </div>
                  <input type="file" accept="image/*" hidden onChange={async e => {
                    const f = e.target.files[0]; if (!f) return;
                    const reader = new FileReader();
                    reader.onload = ev => setSdIn(s => ({...s, image: ev.target.result}));
                    reader.readAsDataURL(f);
                  }} />
                </label>
              </div>
              <div style={{ flex:1, display:'flex', gap:8, alignItems:'flex-end' }}>
                <button style={btn()} onClick={async () => {
                  if (!sdIn.name || !sdIn.code) return alert('Code and Name required');
                  const id = await saveToDB('common', 'SEAM_DIAGRAM', sdIn);
                  const entry = { ...sdIn, id: editId || id };
                  setDb(p => ({ ...p, seamDiagrams: upsert(p.seamDiagrams, entry, editId) }));
                  setSdIn(blankSd); setEditId(null);
                }}>{editId ? 'Update' : 'Add Seam Diagram'}</button>
                {editId && <button style={btn('#f1f5f9','#475569')} onClick={() => cancelEdit(setSdIn, blankSd)}>Cancel</button>}
              </div>
            </div>

            {/* Seam diagram grid */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:12 }}>
              {(db.seamDiagrams||[]).length === 0 && (
                <div style={{ color:'#94a3b8', padding:24, gridColumn:'1/-1', textAlign:'center' }}>No seam diagrams yet. Add the first one above.</div>
              )}
              {(db.seamDiagrams||[]).map(s => (
                <div key={s.id} style={{ border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden', background:'white' }}>
                  {s.image
                    ? <img src={s.image} alt={s.name} style={{ width:'100%', height:100, objectFit:'contain', background:'#f8fafc', borderBottom:'1px solid #f1f5f9' }} />
                    : <div style={{ width:'100%', height:100, background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', color:'#94a3b8', fontSize:12, borderBottom:'1px solid #f1f5f9' }}>No diagram</div>}
                  <div style={{ padding:10 }}>
                    <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:4 }}>
                      <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:12, color:'#4f46e5' }}>{s.code}</span>
                      <span style={{ fontWeight:600, fontSize:13, color:'#1e293b' }}>{s.name}</span>
                    </div>
                    {s.description && <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>{s.description}</div>}
                    {s.machineHint && <div style={{ fontSize:10, color:'#059669' }}>Machine: {s.machineHint}</div>}
                    {s.manualHint  && <div style={{ fontSize:10, color:'#7c3aed' }}>Manual: {s.manualHint}</div>}
                    <div style={{ display:'flex', gap:6, marginTop:8 }}>
                      <button style={btn('#f1f5f9','#475569')} onClick={() => { setSdIn(s); setEditId(s.id); }}>Edit</button>
                      <button style={btn('#fef2f2','#dc2626')} onClick={() => confirmDel('seamDiagrams', s.id, 'SEAM_DIAGRAM', 'common')}>Del</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
